import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, EMPTY } from 'rxjs';
import { concatMap, switchMap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';

import * as appointmentsActions from '@appointments/store/actions/appointments.actions';
import * as commentsActions from '@comments/store/actions/comments.actions';
import * as foldersActions from '@folders/store/actions/folders.actions';
import * as listingActivityActions from '@listings/store/actions/listing-activity.actions';
import * as listingsActions from '@listings/store/actions/listings.actions';
import { ListingActivityConstants } from '@listings/constants/listing-activity-constants';
import { ListingsStoreService } from '@listings/store/services/listings-store.service';
import { UserStoreService } from '@auth/store/services/user-store.service';
import { InterestedBuyerApiService } from '@listings/store/services/interested-buyer-api.service';

@Injectable()
export class BuyersEffects {
    private readonly actions$ = inject(Actions);
    private readonly listingsStoreService = inject(ListingsStoreService);
    private readonly userStoreService = inject(UserStoreService);
    private readonly interestedBuyerApiService = inject(InterestedBuyerApiService);

    public readonly trackInterestedBuyers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                appointmentsActions.createListingAppointmentSuccess,
                commentsActions.createListingCommentSuccess,
                foldersActions.manageListingsFoldersSuccess,
                listingActivityActions.setListingsActivitySuccess,
                listingsActions.listingShared
            ),
            concatLatestFrom(() => [
                this.listingsStoreService.baseListings$,
                this.userStoreService.getUser()
            ]),
            concatMap(([action, listings, user]) => {
                if (action.type === appointmentsActions.createListingAppointmentSuccess.type) {
                    const { request } = action as ReturnType<typeof appointmentsActions.createListingAppointmentSuccess>;
                    const listing = listings[request.listingId];

                    if (listing?.hashCode != null) {
                        return this.interestedBuyerApiService.addInterestedBuyer(user.customerId, listing.hashCode);
                    }
                }
                else if (action.type === commentsActions.createListingCommentSuccess.type) {
                    const { request } = action as ReturnType<typeof commentsActions.createListingCommentSuccess>;
                    const listing = listings[request.listingId];

                    if (listing?.hashCode != null) {
                        return this.interestedBuyerApiService.addInterestedBuyer(user.customerId, listing.hashCode);
                    }
                }
                else if (action.type === foldersActions.manageListingsFoldersSuccess.type) {
                    const { activitiesSet } = action as ReturnType<typeof foldersActions.manageListingsFoldersSuccess>;

                    const listingHashCodes = [...new Set(activitiesSet.map(({ listingHashCode }) => listingHashCode))];

                    return listingHashCodes.length > 0
                        ? this.interestedBuyerApiService.addInterestedBuyers(user.customerId, listingHashCodes)
                        : EMPTY;
                }
                else if (action.type === listingActivityActions.setListingsActivitySuccess.type) {
                    const { request } = action as ReturnType<typeof listingActivityActions.setListingsActivitySuccess>;

                    if (!request.isReactionRemoved) {
                        const isInterestActivity = request.activity.id === ListingActivityConstants.Liked.id
                            || request.activity.id === ListingActivityConstants.PickListed.id;

                        if (isInterestActivity) {
                            const requests = request.listingCandidates.map(candidate => {
                                const listing = listings[candidate.id];

                                if (listing?.hashCode != null) {
                                    return this.interestedBuyerApiService.addInterestedBuyer(user.customerId, listing.hashCode);
                                }
                            });

                            return combineLatest(requests);
                        }
                    }
                }
                else if (action.type === listingsActions.listingShared.type) {
                    const { listingId } = action as ReturnType<typeof listingsActions.listingShared>;

                    return this.interestedBuyerApiService.recordListingShare(user.customerId, listingId, user.agentId).pipe(
                        switchMap(() => this.interestedBuyerApiService.addInterestedBuyer(user.customerId, listingId))
                    );
                }
            })
        ),
        { dispatch: false });
}
